import { useEffect, useRef, useState } from "react";
import { Search, Loader2, X, Copy, Package, ChevronLeft, ChevronRight } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useCatalogBrowse, useStoreFilter, useWholesaleContext } from "@/hooks/use-wholesale";
import { useDebounce } from "@/hooks/use-debounce";
import { fmtKmRange } from "@/lib/wholesale-format";
import { cn } from "@/lib/utils";
import { copySku, stockText, type CatalogSelection } from "@/components/wholesale/catalog-item-sheet";
import type { CatalogRow, CatalogSort } from "@/services/wholesale-catalog";

const ALL = "__all__";
const PER_PAGE = 30;

const SORTS: { value: CatalogSort; label: string }[] = [
  { value: "name", label: "Naziv A–Ž" },
  { value: "newest", label: "Najnovije" },
  { value: "price_desc", label: "Cijena, veća prvo" },
  { value: "price_asc", label: "Cijena, manja prvo" },
  { value: "stock_desc", label: "Zaliha, veća prvo" },
];

/** Redni broj stavke u cijelom katalogu, nula-dopunjen na širinu najvećeg broja —
 *  stranice tako ostaju dijelovi jedne knjige stanja, a ne odvojene liste. */
function ordinal(n: number, total: number): string {
  return String(n).padStart(Math.max(4, String(total).length), "0");
}

/** Red kataloga je proizvod, pa ključ ne nosi varijaciju. */
function itemKey(it: { storeId: string; wcProductId: number }): string {
  return `${it.storeId}:${it.wcProductId}`;
}

function fmtCount(n: number): string {
  return n.toLocaleString("bs-BA");
}

export function CatalogBrowser({ onOpen }: { onOpen: (sel: CatalogSelection) => void }) {
  const [q, setQ] = useState("");
  const { store, setStore } = useStoreFilter(ALL);
  const [inStock, setInStock] = useState(false);
  const [sort, setSort] = useState<CatalogSort>("name");
  const [page, setPage] = useState(1);
  const debounced = useDebounce(q);

  const ctx = useWholesaleContext();
  const sources = ctx.data?.sources ?? [];
  const { data, isFetching, error } = useCatalogBrowse({
    q: debounced, storeId: store === ALL ? "" : store, inStock, sort, page, perPage: PER_PAGE,
  });

  const items = data?.items ?? [];
  const total = data?.total ?? 0;
  const totalPages = data?.totalPages ?? 0;
  const from = data?.from ?? 0;

  // Svaka promjena filtera vraća na prvu stranicu — inače bi lista ostala na
  // stranici koje s novim filterom više nema.
  useEffect(() => setPage(1), [debounced, store, inStock, sort]);
  // Server zna vratiti zadnju stranicu kad je tražena van opsega; slijedi ga da
  // strelice ne skaču s nepostojeće stranice.
  useEffect(() => {
    if (data && data.page !== page) setPage(data.page);
  }, [data?.page]);

  // Tastatura: strelice biraju red, Enter otvara detalje, fokus ostaje u pretrazi.
  const [active, setActive] = useState(0);
  const rowsRef = useRef<Map<string, HTMLButtonElement>>(new Map());
  useEffect(() => setActive(0), [debounced, store, inStock, sort, page]);
  useEffect(() => {
    const it = items[active];
    if (it) rowsRef.current.get(itemKey(it))?.scrollIntoView({ block: "nearest" });
  }, [active, items]);

  function handleKeyDown(e: React.KeyboardEvent<HTMLInputElement>) {
    if (items.length === 0) return;
    if (e.key === "ArrowDown") {
      e.preventDefault();
      setActive((i) => (i + 1) % items.length);
    } else if (e.key === "ArrowUp") {
      e.preventDefault();
      setActive((i) => (i - 1 + items.length) % items.length);
    } else if (e.key === "Enter") {
      e.preventDefault();
      const it = items[active];
      if (it) onOpen({ storeId: it.storeId, wcProductId: it.wcProductId, wcVariationId: it.wcVariationId });
    }
  }

  const filtered = debounced.trim().length > 0 || inStock || store !== ALL;

  return (
    <div className="flex flex-col min-h-0">
      <div className="sticky top-0 z-10 bg-background flex flex-col gap-2 pt-2 -mt-2 pb-2 sm:pb-3">
        <div className="flex items-center gap-2">
          <div className="relative flex-1 sm:max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:left-2.5 sm:h-3.5 sm:w-3.5 text-muted-foreground" />
            <Input
              className="h-11 text-base pl-10 pr-10 sm:h-8 sm:text-xs sm:pl-8 sm:pr-8"
              placeholder="Pretraži po nazivu ili SKU…"
              value={q}
              inputMode="search"
              enterKeyHint="search"
              autoCapitalize="off"
              autoCorrect="off"
              spellCheck={false}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={handleKeyDown}
            />
            {isFetching ? (
              <Loader2 className="absolute right-3 top-1/2 -translate-y-1/2 h-4 w-4 sm:right-2.5 sm:h-3.5 sm:w-3.5 animate-spin text-muted-foreground" />
            ) : q ? (
              <button
                type="button"
                onClick={() => setQ("")}
                aria-label="Očisti pretragu"
                className="absolute right-0 top-0 h-full w-10 sm:w-8 flex items-center justify-center text-muted-foreground hover:text-foreground"
              >
                <X className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
              </button>
            ) : null}
          </div>

          {sources.length > 1 && (
            <Select value={store} onValueChange={setStore}>
              <SelectTrigger className="w-40 shrink-0 hidden sm:flex">
                <SelectValue placeholder="Svi shopovi" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL}>Svi shopovi</SelectItem>
                {sources.map((s) => (
                  <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          <Select value={sort} onValueChange={(v) => setSort(v as CatalogSort)}>
            <SelectTrigger className="w-44 shrink-0 hidden sm:flex">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {SORTS.map((s) => (
                <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <button
            type="button"
            onClick={() => setInStock((v) => !v)}
            aria-pressed={inStock}
            className={cn(
              "shrink-0 h-11 sm:h-8 px-3 text-xs border transition-colors",
              inStock ? "bg-foreground text-background border-foreground" : "border-input text-muted-foreground hover:text-foreground",
            )}
          >
            Na zalihi
          </button>
        </div>

        {/* Na telefonu shop i sort su redovi čipova — jedan dodir umjesto padajuće liste. */}
        <div className="flex sm:hidden items-center gap-1.5 overflow-x-auto -mx-4 px-4 pb-0.5">
          {sources.length > 1 &&
            [{ id: ALL, name: "Svi shopovi" }, ...sources].map((s) => (
              <button
                key={s.id}
                type="button"
                onClick={() => setStore(s.id)}
                className={cn(
                  "shrink-0 h-8 px-3 text-xs border transition-colors",
                  store === s.id ? "bg-foreground text-background border-foreground font-medium" : "border-input text-muted-foreground",
                )}
              >
                {s.name}
              </button>
            ))}
          {sources.length > 1 && <span className="shrink-0 w-px h-6 bg-border mx-1" aria-hidden />}
          {SORTS.map((s) => (
            <button
              key={s.value}
              type="button"
              onClick={() => setSort(s.value)}
              className={cn(
                "shrink-0 h-8 px-3 text-xs border transition-colors",
                sort === s.value ? "bg-foreground text-background border-foreground font-medium" : "border-input text-muted-foreground",
              )}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-destructive text-xs py-2">{(error as Error).message}</p>}

      {sources.length === 0 && !ctx.isLoading ? (
        <p className="text-xs text-muted-foreground py-4 sm:py-10 sm:px-8 sm:ring-1 sm:ring-foreground/10 sm:text-center">
          Nijedan shop nije uključen u veleprodaju. Uključi ga u postavkama veleprodaje.
        </p>
      ) : isFetching && items.length === 0 ? (
        <div className="flex flex-col gap-1.5">
          {Array.from({ length: 6 }).map((_, i) => <Skeleton key={i} className="h-16 sm:h-12 w-full" />)}
        </div>
      ) : items.length === 0 ? (
        <p className="text-xs text-muted-foreground py-4 sm:py-10 sm:px-8 sm:ring-1 sm:ring-foreground/10 sm:text-center">
          {filtered
            ? `Nema artikala za ove filtere${debounced.trim() ? ` („${debounced.trim()}")` : ""}. Provjeri SKU ili probaj kraći pojam.`
            : "Katalog je prazan — pokreni sinhronizaciju shopa da se artikli zrcale."}
        </p>
      ) : (
        <div className="ring-1 ring-foreground/10 flex flex-col min-h-0">
          <div className="hidden sm:flex items-center gap-2.5 px-2 py-1.5 border-b bg-muted/50 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
            <span className="w-10 shrink-0">#</span>
            <span className="w-10 shrink-0" />
            <span className="flex-1">Artikal</span>
            <span className="w-28 shrink-0">Shop</span>
            <span className="w-16 text-right shrink-0">Zaliha</span>
            <span className="w-32 text-right shrink-0">Cijena</span>
            <span className="w-7 shrink-0" />
          </div>

          <div className="flex flex-col divide-y">
            {items.map((it: CatalogRow, i: number) => {
              const out = (it.stockQuantity ?? 0) <= 0;
              return (
                <div
                  key={itemKey(it)}
                  className={cn("group flex items-stretch", i === active && "sm:bg-muted/60")}
                  onMouseEnter={() => setActive(i)}
                >
                  <button
                    ref={(el) => {
                      if (el) rowsRef.current.set(itemKey(it), el);
                      else rowsRef.current.delete(itemKey(it));
                    }}
                    type="button"
                    onClick={() => onOpen({ storeId: it.storeId, wcProductId: it.wcProductId, wcVariationId: it.wcVariationId })}
                    className="flex-1 min-w-0 flex items-center gap-3 p-3 sm:gap-2.5 sm:px-2 sm:py-2 text-left text-xs transition-colors active:bg-muted"
                  >
                    <span className="w-8 sm:w-10 shrink-0 text-[10px] tabular-nums text-muted-foreground/60">
                      {ordinal(from + i, total)}
                    </span>

                    {it.imageUrl ? (
                      <img
                        src={it.imageUrl}
                        alt=""
                        className={cn(
                          "h-14 w-14 sm:h-10 sm:w-10 object-cover shrink-0 ring-1 ring-foreground/10",
                          out && "grayscale opacity-60",
                        )}
                        loading="lazy"
                      />
                    ) : (
                      <div className="h-14 w-14 sm:h-10 sm:w-10 shrink-0 bg-muted ring-1 ring-foreground/10 flex items-center justify-center">
                        <Package className="h-5 w-5 sm:h-4 sm:w-4 text-muted-foreground/50" />
                      </div>
                    )}

                    <span className="flex flex-col gap-0.5 min-w-0 flex-1">
                      <span className="text-sm sm:text-xs font-medium leading-snug line-clamp-2 sm:line-clamp-none sm:truncate">
                        {it.name || "Bez naziva"}
                      </span>
                      <span className="text-[11px] sm:text-[10px] sm:uppercase sm:tracking-[0.08em] text-muted-foreground tabular-nums truncate">
                        {it.sku || "bez SKU"}
                        {it.variations.length > 0 && ` · ${it.variations.length} var.`}
                        <span className="sm:hidden"> · {it.storeName}</span>
                      </span>
                      <span className="flex sm:hidden items-baseline gap-2 pt-0.5">
                        <span className="text-sm font-semibold tabular-nums">{fmtKmRange(it.listPrice, it.priceMax)}</span>
                        <span className={cn("text-[11px] tabular-nums", out ? "text-destructive" : "text-muted-foreground")}>
                          {stockText(it.stockQuantity)}
                        </span>
                      </span>
                    </span>

                    <span className="hidden sm:block w-28 shrink-0">
                      <Badge variant="outline">{it.storeName}</Badge>
                    </span>
                    <span className={cn("hidden sm:block w-16 text-right tabular-nums shrink-0", out ? "text-destructive" : "text-muted-foreground")}>
                      {stockText(it.stockQuantity)}
                    </span>
                    <span className="hidden sm:block w-32 text-right tabular-nums font-medium shrink-0">
                      {fmtKmRange(it.listPrice, it.priceMax)}
                    </span>
                  </button>

                  <button
                    type="button"
                    onClick={() => copySku(it.sku)}
                    aria-label={`Kopiraj SKU ${it.sku || "artikla"}`}
                    title="Kopiraj SKU"
                    className="w-11 sm:w-7 shrink-0 flex items-center justify-center text-muted-foreground hover:text-foreground hover:bg-muted transition-colors"
                  >
                    <Copy className="h-4 w-4 sm:h-3.5 sm:w-3.5" />
                  </button>
                </div>
              );
            })}
          </div>

          <Pager
            from={from}
            to={data?.to ?? 0}
            total={total}
            page={page}
            totalPages={totalPages}
            onPage={setPage}
          />
        </div>
      )}
    </div>
  );
}

/** Podnožje piše tačan opseg prikazanih stavki u cijelom katalogu, pa strelice i skok
 *  na stranicu. Raspon je važniji od broja stranice: po njemu se zna dokle si stigao. */
function Pager({
  from, to, total, page, totalPages, onPage,
}: { from: number; to: number; total: number; page: number; totalPages: number; onPage: (p: number) => void }) {
  const [draft, setDraft] = useState(String(page));
  useEffect(() => setDraft(String(page)), [page]);

  function commit() {
    const n = Number(draft);
    if (!Number.isFinite(n) || n < 1) return setDraft(String(page));
    onPage(Math.min(Math.max(1, Math.trunc(n)), Math.max(1, totalPages)));
  }

  return (
    <div className="border-t flex items-center justify-between gap-2 px-2 py-2 text-[10px] uppercase tracking-[0.08em] text-muted-foreground">
      <span className="tabular-nums">
        {ordinal(from, total)}–{ordinal(to, total)} <span className="text-muted-foreground/50">/</span> {fmtCount(total)}
      </span>

      {totalPages > 1 && (
        <span className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => onPage(page - 1)}
            disabled={page <= 1}
            aria-label="Prethodna stranica"
            className="h-7 w-7 flex items-center justify-center border border-input disabled:opacity-30 hover:bg-muted transition-colors"
          >
            <ChevronLeft className="h-3.5 w-3.5" />
          </button>
          <input
            value={draft}
            onChange={(e) => setDraft(e.target.value.replace(/\D/g, ""))}
            onBlur={commit}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                commit();
              }
            }}
            aria-label="Skoči na stranicu"
            inputMode="numeric"
            className="h-7 w-10 border border-input bg-background text-center text-xs tabular-nums text-foreground focus:outline-none focus:ring-1 focus:ring-ring"
          />
          <span className="tabular-nums">/ {fmtCount(totalPages)}</span>
          <button
            type="button"
            onClick={() => onPage(page + 1)}
            disabled={page >= totalPages}
            aria-label="Sljedeća stranica"
            className="h-7 w-7 flex items-center justify-center border border-input disabled:opacity-30 hover:bg-muted transition-colors"
          >
            <ChevronRight className="h-3.5 w-3.5" />
          </button>
        </span>
      )}

      <span className="hidden sm:flex items-center gap-1.5">
        <kbd className="border px-1 py-px font-sans">↑</kbd>
        <kbd className="border px-1 py-px font-sans">↓</kbd>
        kretanje
        <span className="text-muted-foreground/40">·</span>
        <kbd className="border px-1 py-px font-sans">⏎</kbd>
        detalji
      </span>
    </div>
  );
}
